import { zodResolver } from "@hookform/resolvers/zod";
import { CircularProgress, Grid, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  Controller,
  FormProvider,
  Resolver,
  SubmitHandler,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { z } from "zod";

import { useCurrentOrg, useCurrentOrgId } from "@/providers/auth/hooks";
import { CustomFieldValuesSection } from "@/shared/components/CustomFields";
import {
  EventDataEntityType,
  EventDurationForm,
  EventNameInput,
  EventScopeForm,
  EventSource,
  EventSourceType,
  extendCreateEventFormSchema,
} from "@/shared/components/events";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { TagsEditor } from "@/shared/components/TagsEditor";
import { TargetEntityType } from "@/shared/domain/customFields";
import { EventRecord } from "@/shared/domain/events";
import { hasSessionEvents, legacyEventsWritable } from "@/shared/domain/orgs";
import { useOrg, useOrgEventModel } from "@/shared/domain/orgs/hooks";
import { useVizState } from "@/shared/state/visualization";
import { nanoSecToSecStr } from "@/shared/time";

import { Unit } from "../../events/schema";
import { MetadataForm, MetadataView } from "../../Metadata";
import { RobotoButton } from "../../RobotoButton";
import { AutocompleteType } from "../../TagAndMetadataAutocomplete";
import { autoColorHexForName } from "../colorScale";
import { useEphemeralWorkspaceState } from "../WorkspaceCtx";
import { useCreateWorkspaceEvent } from "../WorkspaceCtx/useWorkspaceEvents";

import styles from "./CreateWorkspaceEventForm.module.css";
import {
  CreateWorkspaceEventFormDefaults,
  makeCreateEventRequest,
  makeCreateSessionEventRequest,
  sessionForSelection,
  type CreateWorkspaceEventForm as CreateEventForm,
} from "./makeCreateEventRequest";

interface CreateEventFormProps {
  defaultFields?: CreateWorkspaceEventFormDefaults;
  onCancel: () => void;
  onSuccess: (event: EventRecord) => void;
  sourceOptions: Array<EventSource>;
}

const workspaceEventFormSchema = extendCreateEventFormSchema({
  scope: z.object({
    dataEntity: z.object({
      type: z.string(),
    }),
    source: z.object({
      type: z.string(),
      panelId: z.string().optional(),
    }),
  }),
});

function mergeDefaultFormValues(
  defaultFields: CreateWorkspaceEventFormDefaults,
  includeSession: boolean,
): CreateEventForm {
  const defaults = {
    name: "",
    description: "",
    metadata: {},
    tags: [],
    scope: {
      // In a workspace that shows a session, a new event defaults to belonging
      // to that session.
      dataEntity: {
        type: includeSession
          ? EventDataEntityType.ThisSession
          : EventDataEntityType.AllFiles,
      },
      source: { type: EventSourceType.AllPanels },
    },
    unit: Unit.Sec,
  };

  // Convert start_time and end_time bigints to strings because
  // the form requires them as strings.
  const overrideDefaultsWithStartEndTimes = {
    ...defaultFields,
    start_time: nanoSecToSecStr(defaultFields?.start_time ?? 0n),
    end_time: nanoSecToSecStr(defaultFields?.end_time ?? 0n),
  };

  return {
    ...defaults,
    ...overrideDefaultsWithStartEndTimes,
  };
}

const AUTO_COLOR_DEBOUNCE_MS = 1000;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, delayMs]);
  return debounced;
}

// Isolates `name` subscription and auto-color recomputation to the picker row
// so the outer form does not re-render on every keystroke in the name field.
// The auto color is debounced so the picker does not flicker through hues as
// the user types.
const EventColorPicker = () => {
  const { control } = useFormContext<CreateEventForm>();
  const name = useWatch({ control, name: "name" });
  const debouncedName = useDebouncedValue(name, AUTO_COLOR_DEBOUNCE_MS);
  const autoColor = useMemo(
    () => autoColorHexForName(debouncedName),
    [debouncedName],
  );

  return (
    <Controller
      control={control}
      name="color"
      render={({ field }) => (
        <div className={styles.colorRow}>
          <TextField
            variant="outlined"
            margin="dense"
            size="small"
            className={styles.colorInput}
            inputProps={{
              type: "color",
              value: field.value || autoColor,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                field.onChange(e.target.value);
              },
            }}
          />
          {field.value && (
            <button
              type="button"
              onClick={() => field.onChange("")}
              className={styles.clearButton}
            >
              Clear
            </button>
          )}
          <RobotoTypography className={styles.colorLabel}>
            {field.value ? "Custom color" : "Auto-generated"}
          </RobotoTypography>
        </div>
      )}
    />
  );
};

/**
 * Shown in place of the form when no event can be created yet: why, any
 * remedy passed as `children`, and Cancel.
 */
function FormUnavailableNotice({
  message,
  onCancel,
  children,
}: {
  message: string;
  onCancel: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <RobotoTypography>{message}</RobotoTypography>
      {children}
      <RobotoButton
        eventName={"CreateWorkspaceEventCanceled"}
        type="button"
        variant="text"
        onClick={onCancel}
      >
        Cancel
      </RobotoButton>
    </div>
  );
}

/**
 * Form for an event on the workspace's data. Offers "This Session" when the
 * workspace shows a session and the session's own org has session events, and
 * the dataset event scopes when the current org's dataset events can be
 * written. The session's org is the one the new event belongs to, and need not
 * be the current org. Waits for both orgs' event models, since they decide the
 * default scope, and offers a retry when an org record cannot be fetched.
 */
export const CreateWorkspaceEventForm = (props: CreateEventFormProps) => {
  const workspaceState = useEphemeralWorkspaceState();
  const selectionSession = sessionForSelection(
    workspaceState.sessions,
    props.defaultFields?.start_time,
  );
  const sessionOrgId = selectionSession?.record.org_id;
  const eventModel = useOrgEventModel();
  const sessionEventModel = useOrgEventModel(sessionOrgId);
  // The records the event models read; their error state tells a failed fetch
  // from one still loading.
  const orgQuery = useOrg(useCurrentOrg()?.org_id);
  const sessionOrgQuery = useOrg(sessionOrgId);
  if (
    eventModel === undefined ||
    (sessionOrgId !== undefined && sessionEventModel === undefined)
  ) {
    const failedQuery = [orgQuery, sessionOrgQuery].find(
      (query) => query.isError,
    );
    if (failedQuery === undefined) {
      return <CircularProgress size={"1.5rem"} aria-label="Loading" />;
    }
    return (
      <FormUnavailableNotice
        message="Your organization's settings could not be loaded, so the event cannot be created yet."
        onCancel={props.onCancel}
      >
        <RobotoButton
          eventName={"CreateWorkspaceEventOrgRetried"}
          type="button"
          variant="text"
          onClick={() => void failedQuery.refetch()}
        >
          Retry
        </RobotoButton>
      </FormUnavailableNotice>
    );
  }

  const includeSession =
    sessionOrgId !== undefined && hasSessionEvents(sessionEventModel);
  const includeDataScopes = legacyEventsWritable(eventModel);
  if (!includeSession && !includeDataScopes) {
    return (
      <FormUnavailableNotice
        message="Events in this organization belong to sessions. Open a session in the workspace to create one."
        onCancel={props.onCancel}
      />
    );
  }

  return (
    <CreateWorkspaceEventFormFields
      {...props}
      includeSession={includeSession}
      includeDataScopes={includeDataScopes}
      sessionOrgId={sessionOrgId}
    />
  );
};

/** No custom-field values; what the form starts with in each org. */
const NO_CUSTOM_FIELD_VALUES: Record<string, unknown> = {};

const CreateWorkspaceEventFormFields = ({
  defaultFields,
  onSuccess,
  onCancel,
  sourceOptions,
  includeSession,
  includeDataScopes,
  sessionOrgId,
}: CreateEventFormProps & {
  includeSession: boolean;
  includeDataScopes: boolean;
  /** The org of the workspace session This Session creates the event on. */
  sessionOrgId: string | undefined;
}) => {
  const workspaceState = useEphemeralWorkspaceState();
  const vizState = useVizState();
  const currentOrgId = useCurrentOrgId();
  const form = useForm<CreateEventForm>({
    defaultValues: mergeDefaultFormValues(defaultFields ?? {}, includeSession),
    resolver: zodResolver(
      workspaceEventFormSchema,
      // TODO workspace schema and CreateEventForm have drifted
    ) as unknown as Resolver<CreateEventForm>,
  });

  // The workspace's sessions can load after the form opens; once one has, a
  // new event defaults to belonging to it, unless the user picked a scope.
  useEffect(() => {
    if (
      includeSession &&
      !form.getFieldState("scope.dataEntity.type").isDirty
    ) {
      form.setValue("scope.dataEntity.type", EventDataEntityType.ThisSession);
    }
  }, [includeSession, form]);

  // The org the new event will belong to, whose custom fields the form offers:
  // the session's for This Session, the current org's for a dataset scope.
  const scopeType = useWatch({
    control: form.control,
    name: "scope.dataEntity.type",
  });
  const eventOrgId =
    scopeType === EventDataEntityType.ThisSession && sessionOrgId !== undefined
      ? sessionOrgId
      : currentOrgId;
  // Values are kept with the org whose fields they fill, so a scope change
  // that moves the event to another org starts its custom fields empty.
  const [customFieldDraft, setCustomFieldDraft] = useState<{
    orgId: string;
    values: Record<string, unknown>;
  }>({ orgId: eventOrgId, values: NO_CUSTOM_FIELD_VALUES });
  const customFieldValues =
    customFieldDraft.orgId === eventOrgId
      ? customFieldDraft.values
      : NO_CUSTOM_FIELD_VALUES;

  const createWorkspaceEvent = useCreateWorkspaceEvent();

  const onSubmit: SubmitHandler<CreateEventForm> = (data) => {
    const request =
      data.scope.dataEntity.type === EventDataEntityType.ThisSession
        ? makeCreateSessionEventRequest(data, workspaceState)
        : makeCreateEventRequest(data, vizState, workspaceState);
    const withCustomFields =
      Object.keys(customFieldValues).length > 0
        ? { ...request, custom_fields: customFieldValues }
        : request;
    return createWorkspaceEvent.mutateAsync(withCustomFields).then(onSuccess);
  };

  const submissionErrors = form.formState.errors.root?.serverErrors;

  return (
    <FormProvider {...form}>
      <form
        onSubmit={(event) => {
          form
            .handleSubmit(onSubmit)(event)
            .catch(() => {
              // Display a user friendly error when the request fails.
              form.setError("root.serverErrors", {
                message: "Failed to create event. Please try again.",
              });
            });
        }}
        className={styles.form}
      >
        <Grid container direction="column" justifyContent="center" spacing={2}>
          <Grid item xs={12}>
            <EventScopeForm
              sourceOptions={sourceOptions}
              includeSession={includeSession}
              includeDataScopes={includeDataScopes}
            />
          </Grid>
          <Grid item xs={12}>
            <EventDurationForm />
          </Grid>
          <Grid item xs={12}>
            <RobotoTypography component={"h3"} className={styles.sectionTitle}>
              Event name
            </RobotoTypography>
            <Controller
              control={form.control}
              name="name"
              render={({ field }) => {
                return (
                  <EventNameInput
                    currentName={field.value}
                    onNameChanged={field.onChange}
                    error={form.formState.errors.name !== undefined}
                    helperText={form.formState.errors.name?.message}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <RobotoTypography component={"h3"} className={styles.sectionTitle}>
              Description (optional)
            </RobotoTypography>
            <TextField
              {...form.register("description")}
              placeholder="Description"
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              error={form.formState.errors.description !== undefined}
              helperText={form.formState.errors.description?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <RobotoTypography component={"h3"} className={styles.sectionTitle}>
              Color (optional)
            </RobotoTypography>
            <EventColorPicker />
          </Grid>
          <Grid item xs={12}>
            <RobotoTypography component={"h3"} className={styles.sectionTitle}>
              Tags (optional)
            </RobotoTypography>
            <Controller
              control={form.control}
              name="tags"
              render={({ field }) => {
                return (
                  <TagsEditor
                    mode="controlled"
                    variant="field"
                    tags={field.value}
                    autocompleteType={AutocompleteType.EventTags}
                    onAdd={(tag) => {
                      field.onChange([...field.value, tag]);
                    }}
                    onRemove={(tag) => {
                      field.onChange(field.value.filter((t) => t !== tag));
                    }}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <RobotoTypography
              component={"h3"}
              className={styles.sectionTitleNoBottomMargin}
            >
              Metadata (optional)
            </RobotoTypography>
            <Controller
              control={form.control}
              name="metadata"
              render={({ field }) => {
                return (
                  <>
                    <MetadataView
                      metadata={field.value}
                      onChange={(metadata) => {
                        field.onChange(metadata);
                      }}
                    />
                    <MetadataForm
                      metadata={field.value}
                      autocompleteType={AutocompleteType.EventMetadataKeys}
                      onAddMetadata={(key, value) => {
                        field.onChange({
                          ...field.value,
                          [key]: value,
                        });
                      }}
                    />
                  </>
                );
              }}
            />
          </Grid>
          {/* CustomFieldValuesSection renders null when the flag is off or the
              org has no Ready event fields; `&:empty` then collapses this row so
              it adds no grid spacing. */}
          <Grid item xs={12} sx={{ "&:empty": { display: "none" } }}>
            {/* Keyed by org, so an unfinished entry for one org's field
                does not outlive a scope change to another org. */}
            <CustomFieldValuesSection
              key={eventOrgId}
              entityType={TargetEntityType.Event}
              orgId={eventOrgId}
              values={customFieldValues}
              onChange={(values) =>
                setCustomFieldDraft({ orgId: eventOrgId, values })
              }
              header={
                <RobotoTypography
                  component={"h3"}
                  className={styles.sectionTitle}
                >
                  Custom Fields (optional)
                </RobotoTypography>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <div className={styles.buttonRow}>
              <RobotoButton
                eventName={"WorkspaceEventCreated"}
                eventProperties={{ eventName: form.getValues("name") }}
                type="submit"
                variant="contained"
                color="primary"
                disabled={form.formState.isSubmitting}
                endIcon={
                  form.formState.isSubmitting ? (
                    <CircularProgress size={"1rem"} />
                  ) : null
                }
              >
                Create Event
              </RobotoButton>
              <RobotoButton
                eventName={"CreateWorkspaceEventCanceled"}
                type="button"
                variant="text"
                onClick={onCancel}
              >
                Cancel
              </RobotoButton>
            </div>
            {submissionErrors && (
              <p className={styles.errorMessage}>{submissionErrors.message}</p>
            )}
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};
